import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, type Pagina } from '../comun';
import type { components } from '../tipos/plataforma';

/**
 * Operaciones del **portal de partners** (1.23) con su `operationId` y los tipos generados de `plataforma.yaml`. Van fuera
 * de un negocio (`/v1/partner/…`, permiso `partner.portal.ver` de la cuenta de partner) y la solicitud es un alta pública
 * con antibot (`applyAsPartner`, `X-Captcha-Token`, G76; sin `terms_version`: la versión vigente la fija el back, G1099). Los campos de G1089 (`terms_version`, `terms_accepted_at`,
 * `city`, `plan_summary`, `invoice_number`, `invoice_url`) pueden llegar nulos.
 */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Partner = Esquema<'Partner'>;
export type SolicitudPartner = Esquema<'PartnerApplication'>;
export type CambioPerfilPartner = Esquema<'PartnerProfileUpdate'>;
export type NegocioDelPartner = Esquema<'PartnerBusiness'>;
export type ComisionDelPartner = Esquema<'PartnerCommission'>;
export type LiquidacionDelPartner = Esquema<'PartnerSettlement'>;
export type DireccionFiscal = Esquema<'PostalAddress'>;
export type ResumenDelPartner = Esquema<'PartnerSummary'>;

/** Filtros de `listPartnerCommissions` (G1098): mes `AAAA-MM` (hora de Madrid) y negocio atribuido. */
export interface FiltroComisiones extends Pagina {
    period?: string;
    business_id?: string;
}

export function operacionesPartners(api: ClienteApi) {
    return {
        /** applyAsPartner (alta pública con antibot): `202` también si el email ya existía, sin revelarlo (G1090). */
        applyAsPartner: (datos: SolicitudPartner, captcha: string | null, clave = claveIdempotencia()) =>
            api.pedir<undefined>('POST', '/v1/partners/applications', {
                cuerpo: datos,
                idempotencia: clave,
                sinSesion: true,
                cabeceras: captcha === null ? {} : { 'X-Captcha-Token': captcha },
            }),
        /** getPartnerProfile: el partner de la cuenta, con su código, `referral_url`, `tax_id_masked` y `approved_at` (G1089, G1096, G1097); sin aprobar, código y enlace nulos (G1108). */
        getPartnerProfile: () => api.pedir<Partner>('GET', '/v1/partner/me'),
        /** updatePartnerProfile: teléfono, dirección fiscal, email de facturas e IBAN; rechazado o suspendido, `409` (G1090). */
        updatePartnerProfile: (cambios: CambioPerfilPartner, clave = claveIdempotencia()) =>
            api.pedir<Partner>('PATCH', '/v1/partner/me', { cuerpo: cambios, idempotencia: clave }),
        /** listPartnerBusinesses: negocios atribuidos, con `business_id` (G1104), `city` y `plan_summary` (G1089). */
        listPartnerBusinesses: (pagina: Pagina = {}) =>
            api.pedir<{ data: NegocioDelPartner[]; next_cursor: string | null }>('GET', `/v1/partner/businesses${consulta({ ...pagina })}`),
        /**
         * getPartnerSummary: las cifras del portal, calculadas en el servidor (G1094; el panel no suma dinero, G290). Sin
         * `period` (`AAAA-MM`, hora de Madrid), el mes en curso; con él, el de ese mes (G1105). Comisiones brutas, sin restar
         * las líneas negativas: lo neto está en las liquidaciones (G1116). Con `businessId`, las cifras de ese negocio (G1177);
         * uno que no es suyo, `404`.
         */
        getPartnerSummary: (period?: string, businessId?: string) =>
            api.pedir<ResumenDelPartner>('GET', `/v1/partner/summary${consulta({ period, business_id: businessId })}`),
        /** listPartnerCommissions: de la más reciente a la más antigua, con los filtros `period` y `business_id` (G1098, G1104); mal formados, `422` (G1113). */
        listPartnerCommissions: (filtro: FiltroComisiones = {}) =>
            api.pedir<{ data: ComisionDelPartner[]; next_cursor: string | null }>('GET', `/v1/partner/commissions${consulta({ ...filtro })}`),
        /** listPartnerSettlements: de la más reciente a la más antigua, con `invoice_number` e `invoice_url` (G1089). */
        listPartnerSettlements: (pagina: Pagina = {}) =>
            api.pedir<{ data: LiquidacionDelPartner[]; next_cursor: string | null }>('GET', `/v1/partner/settlements${consulta({ ...pagina })}`),
    };
}
